import { useState, useEffect, useRef } from "react";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Search, X, Loader2, CheckCircle2, ArrowLeft, Brain, Filter, Zap, Building2 } from "lucide-react";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Checkbox } from "@/components/ui/checkbox";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import { useTranslation } from "react-i18next";

interface Skill {
	id: string;
	name: string;
	isSoft?: boolean;
}

interface Experience {
	id: string;
	name: string;
	category: string;
}

interface ScoredJob {
	jobId: string;
	jobTitle: string;
	jobStatus: string | null;
	combinedScore: number;
	isAlreadyMatched: boolean;
	matchReasons: string[];
	skillComparison: Array<{
		candidateSkillId: string;
		candidateSkillName: string;
		jobSkillId: string | null;
		jobSkillName: string | null;
		similarity: number;
		matched: boolean;
	}>;
	experienceComparison: Array<{
		candidateExperienceId: string;
		candidateExperienceName: string;
		jobExperienceId: string | null;
		jobExperienceName: string | null;
		similarity: number;
		matched: boolean;
	}>;
}

interface FindJobsModalProps {
	candidateId: string;
	candidateName: string;
	open: boolean;
	onOpenChange: (open: boolean) => void;
}

export function FindJobsModal({ candidateId, candidateName, open, onOpenChange }: FindJobsModalProps) {
	const { t } = useTranslation();
	const progressEndRef = useRef<HTMLDivElement>(null);

	// Skills state
	const [availableSkills, setAvailableSkills] = useState<Skill[]>([]);
	const [requiredSkills, setRequiredSkills] = useState<Skill[]>([]);
	const [excludedSkills, setExcludedSkills] = useState<Skill[]>([]);

	// Experiences state
	const [availableExperiences, setAvailableExperiences] = useState<Experience[]>([]);
	const [requiredExperiences, setRequiredExperiences] = useState<Experience[]>([]);
	const [excludedExperiences, setExcludedExperiences] = useState<Experience[]>([]);

	// Track IDs originally loaded from the candidate profile — used to detect user-added items
	const originalSkillIds = useRef<Set<string>>(new Set());
	const originalExperienceIds = useRef<Set<string>>(new Set());

	// Search parameters
	const [excludeSoftSkills, setExcludeSoftSkills] = useState(true);

	// Drag state
	const [draggedItem, setDraggedItem] = useState<{ id: string; type: 'skill' | 'experience' } | null>(null);
	const [dragOverZone, setDragOverZone] = useState<string | null>(null);

	// Search state
	const [isSearching, setIsSearching] = useState(false);
	const [searchResults, setSearchResults] = useState<{ shortlist: ScoredJob[] } | null>(null);
	const [searchProgress, setSearchProgress] = useState<Array<{
		step: string;
		status: 'pending' | 'running' | 'completed' | 'error';
		data?: any;
	}>>([]);

	// View state
	const [currentView, setCurrentView] = useState<'config' | 'results'>('config');

	// Quick add state
	const [skillSearchQuery, setSkillSearchQuery] = useState('');
	const [experienceSearchQuery, setExperienceSearchQuery] = useState('');
	const [skillSearchResults, setSkillSearchResults] = useState<Skill[]>([]);
	const [experienceSearchResults, setExperienceSearchResults] = useState<Experience[]>([]);
	const [isSearchingSkills, setIsSearchingSkills] = useState(false);
	const [isSearchingExperiences, setIsSearchingExperiences] = useState(false);
	const [recentlyAddedSkills, setRecentlyAddedSkills] = useState<Set<string>>(new Set());
	const [recentlyAddedExperiences, setRecentlyAddedExperiences] = useState<Set<string>>(new Set());

	// Selected jobs for bulk match creation
	const [selectedJobs, setSelectedJobs] = useState<Set<string>>(new Set());
	const [isCreatingMatches, setIsCreatingMatches] = useState(false);
	const [jobSummaries, setJobSummaries] = useState<Record<string, string>>({});

	// Load candidate skills and experiences when modal opens
	useEffect(() => {
		if (open && candidateId) {
			loadCandidateSkillsAndExperiences();
			setCurrentView('config');
			setSearchResults(null);
			setSearchProgress([]);
			setSelectedJobs(new Set());
			setJobSummaries({});
		}
	}, [open, candidateId]);

	// Set up realtime channel
	useEffect(() => {
		if (!open) return;

		let channel: any = null;

		const setupChannel = async () => {
			const { data: { user } } = await supabase.auth.getUser();
			if (!user) return;

			const channelId = `candidate_search:${candidateId}:${user.id}`;
			channel = supabase.channel(channelId);

			channel
				.on('broadcast', { event: 'search_progress' }, (payload: any) => {
					const event = payload.payload;

					if (event.step === 'complete') {
						if (event.result?.shortlist) {
							const filtered = event.result.shortlist.filter((j: any) => j.combinedScore >= 50 && j.jobStatus === 'open');
							event.result.shortlist = filtered;
							if (filtered.length > 0) {
								const ids = filtered.map((j: any) => j.jobId);
								supabase.from('jobs').select('id, ai_summary').in('id', ids).then(({ data }) => {
									if (data) {
										const map: Record<string, string> = {};
										for (const row of data)
											if (row.ai_summary) map[row.id] = row.ai_summary;
										setJobSummaries(map);
									}
								});
							}
						}
						setSearchResults(event.result);
						setIsSearching(false);
					} else if (event.step === 'error') {
						toast({
							title: t("errors.error"),
							description: event.message || t("candidateJobSearch.toasts.searchError"),
							variant: "destructive",
						});
						setIsSearching(false);
						setCurrentView('config');
					} else {
						setSearchProgress(prev => {
							const existing = prev.find(p => p.step === event.step);
							if (existing)
								return prev.map(p => p.step === event.step ? event : p);
							return [...prev, event];
						});
					}
				})
				.subscribe();
		};

		setupChannel();

		return () => {
			if (channel)
				supabase.removeChannel(channel);
		};
	}, [open, candidateId, t]);

	// Auto-scroll progress
	useEffect(() => {
		if (searchProgress.length > 0)
			progressEndRef.current?.scrollIntoView({ behavior: 'smooth' });
	}, [searchProgress]);

	// Auto-switch to results view when done
	useEffect(() => {
		if (searchResults)
			setCurrentView('results');
	}, [searchResults]);

	// Handle soft skills toggle
	useEffect(() => {
		if (excludeSoftSkills) {
			const softSkills = availableSkills.filter(s => s.isSoft);
			if (softSkills.length > 0) {
				setAvailableSkills(availableSkills.filter(s => !s.isSoft));
				setExcludedSkills([...excludedSkills, ...softSkills]);
			}
		} else {
			const softSkills = excludedSkills.filter(s => s.isSoft);
			if (softSkills.length > 0) {
				setExcludedSkills(excludedSkills.filter(s => !s.isSoft));
				setAvailableSkills([...availableSkills, ...softSkills]);
			}
		}
	}, [excludeSoftSkills]);

	const loadCandidateSkillsAndExperiences = async () => {
		try {
			const { data: candidateSkillsData, error: skillsError } = await supabase
				.from('candidate_skills')
				.select(`
					normalized_skill_id,
					normalized_skills!inner(id, name, is_soft_skill)
				`)
				.eq('candidate_id', candidateId);

			if (skillsError) throw skillsError;

			const uniqueSkills = candidateSkillsData?.reduce((acc: any[], row: any) => {
				if (!acc.find((item: any) => item.normalized_skills.id === row.normalized_skills.id))
					acc.push(row);
				return acc;
			}, []) || [];

			const skills = uniqueSkills.map((row: any) => ({
				id: row.normalized_skills.id,
				name: row.normalized_skills.name,
				isSoft: row.normalized_skills.is_soft_skill
			}));

			originalSkillIds.current = new Set(skills.map((s: Skill) => s.id));

			const regularSkills = skills.filter((s: Skill) => !s.isSoft);
			const softSkills = skills.filter((s: Skill) => s.isSoft);

			setAvailableSkills(regularSkills);
			setExcludedSkills(softSkills);
			setRequiredSkills([]);

			const { data: candidateExpData, error: expError } = await supabase
				.from('candidate_experiences')
				.select(`
					normalized_experience_id,
					normalized_experiences!inner(id, name, category)
				`)
				.eq('candidate_id', candidateId);

			if (expError) throw expError;

			const uniqueExperiences = candidateExpData?.reduce((acc: any[], row: any) => {
				if (!acc.find((item: any) => item.normalized_experiences.id === row.normalized_experiences.id))
					acc.push(row);
				return acc;
			}, []) || [];

			const experiences = uniqueExperiences.map((row: any) => ({
				id: row.normalized_experiences.id,
				name: row.normalized_experiences.name,
				category: row.normalized_experiences.category
			}));

			originalExperienceIds.current = new Set(experiences.map((e: Experience) => e.id));

			setAvailableExperiences(experiences);
			setRequiredExperiences([]);
			setExcludedExperiences([]);
		} catch (_error) {
			toast({
				title: t("errors.error"),
				description: t("candidateJobSearch.toasts.loadError"),
				variant: "destructive",
			});
		}
	};

	// Quick search for skills
	useEffect(() => {
		if (skillSearchQuery.trim().length >= 2) {
			const delaySearch = setTimeout(() => searchSkills(skillSearchQuery), 300);
			return () => clearTimeout(delaySearch);
		} else {
			setSkillSearchResults([]);
		}
	}, [skillSearchQuery]);

	// Quick search for experiences
	useEffect(() => {
		if (experienceSearchQuery.trim().length >= 2) {
			const delaySearch = setTimeout(() => searchExperiences(experienceSearchQuery), 300);
			return () => clearTimeout(delaySearch);
		} else {
			setExperienceSearchResults([]);
		}
	}, [experienceSearchQuery]);

	const searchSkills = async (query: string) => {
		setIsSearchingSkills(true);
		try {
			const existingIds = [...availableSkills, ...requiredSkills, ...excludedSkills].map(s => s.id);
			// @ts-ignore - table exists but types not yet regenerated
			const { data, error } = await supabase
				.from('org_skills')
				.select('id:normalized_skill_id, name, is_soft_skill')
				.ilike('name', `%${query}%`)
				.not('normalized_skill_id', 'in', existingIds.length > 0 ? `(${existingIds.join(',')})` : '()')
				.limit(10);

			if (error) throw error;

			// @ts-ignore - untyped rows from stale generated types
			setSkillSearchResults(data?.map(item => ({
				id: item.id,
				name: item.name,
				isSoft: item.is_soft_skill
			})) || []);
		} catch (_error) {
			// silently fail skill search
		} finally {
			setIsSearchingSkills(false);
		}
	};

	const searchExperiences = async (query: string) => {
		setIsSearchingExperiences(true);
		try {
			const existingIds = [...availableExperiences, ...requiredExperiences, ...excludedExperiences].map(e => e.id);
			// @ts-ignore - table exists but types not yet regenerated
			const { data, error } = await supabase
				.from('org_experiences')
				.select('id:normalized_experience_id, name, category')
				.ilike('name', `%${query}%`)
				.not('normalized_experience_id', 'in', existingIds.length > 0 ? `(${existingIds.join(',')})` : '()')
				.limit(10);

			if (error) throw error;

			// @ts-ignore - untyped rows from stale generated types
			setExperienceSearchResults(data?.map(item => ({
				id: item.id,
				name: item.name,
				category: item.category
			})) || []);
		} catch (_error) {
			// silently fail experience search
		} finally {
			setIsSearchingExperiences(false);
		}
	};

	const cycleSkillState = (skillId: string) => {
		const inAvailable = availableSkills.find(s => s.id === skillId);
		const inRequired = requiredSkills.find(s => s.id === skillId);
		const inExcluded = excludedSkills.find(s => s.id === skillId);

		if (inAvailable) {
			setAvailableSkills(availableSkills.filter(s => s.id !== skillId));
			setRequiredSkills([...requiredSkills, inAvailable]);
		} else if (inRequired) {
			setRequiredSkills(requiredSkills.filter(s => s.id !== skillId));
			setExcludedSkills([...excludedSkills, inRequired]);
		} else if (inExcluded) {
			setExcludedSkills(excludedSkills.filter(s => s.id !== skillId));
			setAvailableSkills([...availableSkills, inExcluded]);
		}
	};

	const cycleExperienceState = (expId: string) => {
		const inAvailable = availableExperiences.find(e => e.id === expId);
		const inRequired = requiredExperiences.find(e => e.id === expId);
		const inExcluded = excludedExperiences.find(e => e.id === expId);

		if (inAvailable) {
			setAvailableExperiences(availableExperiences.filter(e => e.id !== expId));
			setRequiredExperiences([...requiredExperiences, inAvailable]);
		} else if (inRequired) {
			setRequiredExperiences(requiredExperiences.filter(e => e.id !== expId));
			setExcludedExperiences([...excludedExperiences, inRequired]);
		} else if (inExcluded) {
			setExcludedExperiences(excludedExperiences.filter(e => e.id !== expId));
			setAvailableExperiences([...availableExperiences, inExcluded]);
		}
	};

	const removeSkill = (skillId: string) => {
		setAvailableSkills(availableSkills.filter(s => s.id !== skillId));
		setRequiredSkills(requiredSkills.filter(s => s.id !== skillId));
		setExcludedSkills(excludedSkills.filter(s => s.id !== skillId));
	};

	const removeExperience = (expId: string) => {
		setAvailableExperiences(availableExperiences.filter(e => e.id !== expId));
		setRequiredExperiences(requiredExperiences.filter(e => e.id !== expId));
		setExcludedExperiences(excludedExperiences.filter(e => e.id !== expId));
	};

	const moveSkillToState = (skillId: string, targetState: 'required' | 'searched' | 'excluded') => {
		let skill: Skill | undefined;
		for (const list of [availableSkills, requiredSkills, excludedSkills]) {
			const found = list.find(s => s.id === skillId);
			if (found) { skill = found; break; }
		}
		if (!skill) return;

		const newAvailable = availableSkills.filter(s => s.id !== skillId);
		const newRequired = requiredSkills.filter(s => s.id !== skillId);
		const newExcluded = excludedSkills.filter(s => s.id !== skillId);

		if (targetState === 'searched') newAvailable.push(skill);
		else if (targetState === 'required') newRequired.push(skill);
		else newExcluded.push(skill);

		setAvailableSkills(newAvailable);
		setRequiredSkills(newRequired);
		setExcludedSkills(newExcluded);
	};

	const moveExperienceToState = (expId: string, targetState: 'required' | 'searched' | 'excluded') => {
		let exp: Experience | undefined;
		for (const list of [availableExperiences, requiredExperiences, excludedExperiences]) {
			const found = list.find(e => e.id === expId);
			if (found) { exp = found; break; }
		}
		if (!exp) return;

		const newAvailable = availableExperiences.filter(e => e.id !== expId);
		const newRequired = requiredExperiences.filter(e => e.id !== expId);
		const newExcluded = excludedExperiences.filter(e => e.id !== expId);

		if (targetState === 'searched') newAvailable.push(exp);
		else if (targetState === 'required') newRequired.push(exp);
		else newExcluded.push(exp);

		setAvailableExperiences(newAvailable);
		setRequiredExperiences(newRequired);
		setExcludedExperiences(newExcluded);
	};

	const handleDragStart = (e: React.DragEvent, id: string, type: 'skill' | 'experience') => {
		setDraggedItem({ id, type });
		e.dataTransfer.effectAllowed = 'move';
	};

	const handleDragEnd = () => {
		setDraggedItem(null);
		setDragOverZone(null);
	};

	const handleDropOnZone = (e: React.DragEvent, targetState: 'required' | 'searched' | 'excluded', zoneType: 'skill' | 'experience') => {
		e.preventDefault();
		setDragOverZone(null);
		if (!draggedItem || draggedItem.type !== zoneType) return;

		if (draggedItem.type === 'skill')
			moveSkillToState(draggedItem.id, targetState);
		else
			moveExperienceToState(draggedItem.id, targetState);

		setDraggedItem(null);
	};

	const addSkillToSection = (skill: Skill, section: 'available' | 'required' | 'excluded') => {
		if (section === 'available') setAvailableSkills([...availableSkills, skill]);
		else if (section === 'required') setRequiredSkills([...requiredSkills, skill]);
		else setExcludedSkills([...excludedSkills, skill]);

		setRecentlyAddedSkills(new Set([...recentlyAddedSkills, skill.id]));
		setTimeout(() => {
			setRecentlyAddedSkills(prev => {
				const s = new Set(prev);
				s.delete(skill.id);
				return s;
			});
		}, 1000);

		setSkillSearchResults(skillSearchResults.filter(s => s.id !== skill.id));
	};

	const addExperienceToSection = (exp: Experience, section: 'available' | 'required' | 'excluded') => {
		if (section === 'available') setAvailableExperiences([...availableExperiences, exp]);
		else if (section === 'required') setRequiredExperiences([...requiredExperiences, exp]);
		else setExcludedExperiences([...excludedExperiences, exp]);

		setRecentlyAddedExperiences(new Set([...recentlyAddedExperiences, exp.id]));
		setTimeout(() => {
			setRecentlyAddedExperiences(prev => {
				const s = new Set(prev);
				s.delete(exp.id);
				return s;
			});
		}, 1000);

		setExperienceSearchResults(experienceSearchResults.filter(e => e.id !== exp.id));
	};

	const executeSearch = () => {
		setIsSearching(true);
		setSearchResults(null);
		setSearchProgress([]);
		setCurrentView('results');
		setSelectedJobs(new Set());

		const params = new URLSearchParams({
			candidateId,
			flexibilityLevel: 'normal',
			includeSoftSkills: (!excludeSoftSkills).toString(),
		});

		if (requiredSkills.length > 0)
			params.append('mandatorySkillIds', requiredSkills.map(s => s.id).join(','));
		if (requiredExperiences.length > 0)
			params.append('mandatoryExperienceIds', requiredExperiences.map(e => e.id).join(','));
		if (excludedSkills.length > 0)
			params.append('excludedSkillIds', excludedSkills.map(s => s.id).join(','));
		if (excludedExperiences.length > 0)
			params.append('excludedExperienceIds', excludedExperiences.map(e => e.id).join(','));

		// Skills/experiences the user added from outside the candidate's original profile
		const includedSkillIds = [...availableSkills, ...requiredSkills]
			.filter(s => !originalSkillIds.current.has(s.id))
			.map(s => s.id);
		const includedExperienceIds = [...availableExperiences, ...requiredExperiences]
			.filter(e => !originalExperienceIds.current.has(e.id))
			.map(e => e.id);
		if (includedSkillIds.length > 0)
			params.append('includedSkillIds', includedSkillIds.join(','));
		if (includedExperienceIds.length > 0)
			params.append('includedExperienceIds', includedExperienceIds.join(','));

		supabase.functions.invoke(
			`digger-intelligence?command=match-candidate-with-jobs-fast&${params.toString()}`,
			{ method: 'GET' }
		);
	};

	const toggleJobSelection = (jobId: string, isAlreadyMatched: boolean) => {
		if (isAlreadyMatched) return;
		const next = new Set(selectedJobs);
		if (next.has(jobId)) next.delete(jobId);
		else next.add(jobId);
		setSelectedJobs(next);
	};

	const handleCreateSelectedMatches = async () => {
		if (selectedJobs.size === 0) return;

		setIsCreatingMatches(true);
		try {
			const jobIds = Array.from(selectedJobs);

			const { data: existingMatches } = await supabase
				.from('matches')
				.select('job_id')
				.eq('candidate_id', candidateId)
				.in('job_id', jobIds);

			const existingJobIds = new Set(existingMatches?.map(m => m.job_id) || []);
			const newJobIds = jobIds.filter(id => !existingJobIds.has(id));

			if (newJobIds.length === 0) {
				toast({
					description: t("candidateJobSearch.toasts.allAlreadyMatched"),
				});
				setSelectedJobs(new Set());
				return;
			}

			const matchInserts = newJobIds.map(jobId => ({
				job_id: jobId,
				candidate_id: candidateId,
				score: 0,
				status: 'matched'
			}));

			const { data, error } = await supabase
				.from('matches')
				.insert(matchInserts)
				.select('id');

			if (error) throw error;

			for (const match of data) {
				await supabase.functions.invoke('ai-processing?command=push-new-analysis', {
					method: 'POST',
					body: {
						entityId: match.id,
						entityType: "match",
						analysisType: "full-match",
						actionOnComplete: "update-match",
					}
				});
			}

			const skippedCount = jobIds.length - newJobIds.length;

			toast({
				title: t("advancedSearch.toasts.success"),
				description: skippedCount > 0
					? `${t("candidateJobSearch.toasts.added")} ${newJobIds.length} ${t("candidateJobSearch.toasts.jobs")}. ${t("advancedSearch.toasts.alreadyMatchedCount", { count: skippedCount })}`
					: `${t("candidateJobSearch.toasts.added")} ${newJobIds.length} ${t("candidateJobSearch.toasts.jobs")}`,
			});

			setSearchResults(prev => {
				if (!prev) return prev;
				return {
					...prev,
					shortlist: prev.shortlist.map(j =>
						newJobIds.includes(j.jobId) ? { ...j, isAlreadyMatched: true } : j
					)
				};
			});
			setSelectedJobs(new Set());
		} catch (_error) {
			toast({
				title: t("errors.error"),
				description: t("candidateJobSearch.toasts.matchError"),
				variant: "destructive",
			});
		} finally {
			setIsCreatingMatches(false);
		}
	};

	const getStateColor = (state: 'required' | 'searched' | 'excluded') => {
		switch (state) {
			case 'required': return 'bg-emerald-500';
			case 'searched': return 'bg-blue-500';
			case 'excluded': return 'bg-red-400';
		}
	};

	const getChipClasses = (state: 'required' | 'searched' | 'excluded') => {
		switch (state) {
			case 'required':
				return 'border-emerald-300 dark:border-emerald-700 bg-emerald-50 dark:bg-emerald-950/30 text-emerald-800 dark:text-emerald-300';
			case 'searched':
				return 'border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-950/30 text-blue-800 dark:text-blue-300';
			case 'excluded':
				return 'border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-950/30 text-red-400 dark:text-red-500 line-through opacity-60';
		}
	};

	const getScoreColor = (_score: number) => {
		return '';
	};

	const renderDropZone = (
		state: 'required' | 'searched' | 'excluded',
		items: any[],
		type: 'skill' | 'experience',
		label: string,
		labelColor: string,
		showSeparator: boolean
	) => {
		const zoneKey = `${type}-${state}`;
		const isOver = dragOverZone === zoneKey && draggedItem?.type === type;
		const borderHighlight = state === 'required'
			? 'border-emerald-400 bg-emerald-50/50 dark:bg-emerald-950/20'
			: state === 'searched'
				? 'border-blue-400 bg-blue-50/50 dark:bg-blue-950/20'
				: 'border-red-400 bg-red-50/50 dark:bg-red-950/20';

		return (
			<div
				key={state}
				onDragOver={(e) => { e.preventDefault(); setDragOverZone(zoneKey); }}
				onDragLeave={() => setDragOverZone(null)}
				onDrop={(e) => handleDropOnZone(e, state, type)}
				className={`rounded-md p-2 transition-all min-h-[36px] ${isOver ? `border-2 border-dashed ${borderHighlight}` : 'border-2 border-transparent'}`}
			>
				{showSeparator && <div className="border-t border-border/50 dark:border-border/30 -mt-2 mb-2" />}
				<p className={`text-xs font-medium ${labelColor} mb-1.5`}>{label}</p>
				<div className="flex flex-wrap gap-1.5">
					{items.map(item => renderChip(item, type))}
					{items.length === 0 && isOver && (
						<p className="text-xs text-muted-foreground/50 italic">{t("advancedSearch.legend.dropHere")}</p>
					)}
				</div>
			</div>
		);
	};

	const renderChip = (item: (Skill | Experience) & { state: 'required' | 'searched' | 'excluded' }, type: 'skill' | 'experience') => {
		const isExperience = type === 'experience';
		const category = isExperience ? (item as Experience & { state: string }).category : null;

		return (
			<TooltipProvider key={item.id} delayDuration={200}>
				<Tooltip>
					<TooltipTrigger asChild>
						<Badge
							variant="outline"
							draggable
							onDragStart={(e) => handleDragStart(e, item.id, type)}
							onDragEnd={handleDragEnd}
							className={`cursor-grab active:cursor-grabbing select-none transition-all hover:shadow-sm flex items-center gap-1.5 pr-1 ${getChipClasses(item.state)} ${recentlyAddedSkills.has(item.id) || recentlyAddedExperiences.has(item.id) ? 'ring-2 ring-emerald-400 ring-offset-1' : ''}`}
							onClick={() => type === 'skill' ? cycleSkillState(item.id) : cycleExperienceState(item.id)}
						>
							<span className={`h-2 w-2 rounded-full flex-shrink-0 ${getStateColor(item.state)}`} />
							{item.name}
							{'isSoft' in item && item.isSoft && (
								<span className="text-[10px] opacity-50">{t("advancedSearch.skills.soft")}</span>
							)}
							{category && (
								<span className="text-[10px] opacity-50">({category.replace(/_/g, ' ')})</span>
							)}
							<button
								onClick={(e) => {
									e.stopPropagation();
									type === 'skill' ? removeSkill(item.id) : removeExperience(item.id);
								}}
								className="ml-0.5 hover:bg-black/10 dark:hover:bg-white/10 rounded-full p-0.5"
							>
								<X className="h-3 w-3" />
							</button>
						</Badge>
					</TooltipTrigger>
					<TooltipContent side="top" className="text-xs">
						{t("advancedSearch.chipTooltip")}
					</TooltipContent>
				</Tooltip>
			</TooltipProvider>
		);
	};

	const renderSkillChips = () => {
		const groups = {
			required: requiredSkills.map(s => ({ ...s, state: 'required' as const })),
			searched: availableSkills.map(s => ({ ...s, state: 'searched' as const })),
			excluded: excludedSkills.map(s => ({ ...s, state: 'excluded' as const })),
		};

		return (
			<div className="space-y-1">
				{renderDropZone('required', groups.required, 'skill', t("advancedSearch.skills.required"), 'text-emerald-600 dark:text-emerald-400', false)}
				{renderDropZone('searched', groups.searched, 'skill', t("advancedSearch.skills.searched"), 'text-blue-600 dark:text-blue-400', true)}
				{renderDropZone('excluded', groups.excluded, 'skill', t("advancedSearch.skills.excluded"), 'text-red-500 dark:text-red-400', true)}
			</div>
		);
	};

	const renderExperienceChips = () => {
		const groups = {
			required: requiredExperiences.map(e => ({ ...e, state: 'required' as const })),
			searched: availableExperiences.map(e => ({ ...e, state: 'searched' as const })),
			excluded: excludedExperiences.map(e => ({ ...e, state: 'excluded' as const })),
		};

		return (
			<div className="space-y-1">
				{renderDropZone('required', groups.required, 'experience', t("advancedSearch.experiences.required"), 'text-emerald-600 dark:text-emerald-400', false)}
				{renderDropZone('searched', groups.searched, 'experience', t("advancedSearch.experiences.searched"), 'text-blue-600 dark:text-blue-400', true)}
				{renderDropZone('excluded', groups.excluded, 'experience', t("advancedSearch.experiences.excluded"), 'text-red-500 dark:text-red-400', true)}
			</div>
		);
	};

	const renderConfigView = () => (
		<div className="flex flex-col h-full">
			{/* Header */}
			<div className="flex items-center gap-3 pb-4 border-b border-border">
				<div className="p-2 rounded-lg bg-blue-600">
					<Search className="h-5 w-5 text-white" />
				</div>
				<div className="flex-1">
					<h2 className="text-lg font-semibold text-foreground">
						{t("candidateJobSearch.title", { candidateName })}
					</h2>
					<p className="text-xs text-muted-foreground">{t("candidateJobSearch.subtitle")}</p>
				</div>
			</div>

			{/* Legend */}
			<div className="flex items-center gap-4 py-3 text-xs text-muted-foreground">
				<span className="flex items-center gap-1.5">
					<span className="h-2.5 w-2.5 rounded-full bg-emerald-500" />
					{t("advancedSearch.legend.required")}
				</span>
				<span className="flex items-center gap-1.5">
					<span className="h-2.5 w-2.5 rounded-full bg-blue-500" />
					{t("advancedSearch.legend.searched")}
				</span>
				<span className="flex items-center gap-1.5">
					<span className="h-2.5 w-2.5 rounded-full bg-red-400" />
					{t("advancedSearch.legend.excluded")}
				</span>
				<span className="text-muted-foreground/60">— {t("advancedSearch.legend.clickToChange")}</span>
			</div>

			{/* Configuration */}
			<div className="flex-1 overflow-y-auto scrollbar-thin">
				<div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
					{/* Skills */}
					<Card className="card-glass border-border">
						<CardHeader className="pb-3">
							<div className="flex items-center justify-between">
								<CardTitle className="text-sm font-medium text-foreground">{t("advancedSearch.skills.title")}</CardTitle>
								<div className="flex items-center gap-2">
									<input
										type="checkbox"
										id="find-jobs-soft-skills"
										checked={excludeSoftSkills}
										onChange={(e) => setExcludeSoftSkills(e.target.checked)}
										className="h-3.5 w-3.5 rounded"
									/>
									<Label htmlFor="find-jobs-soft-skills" className="cursor-pointer text-xs text-muted-foreground">
										{t("advancedSearch.skills.excludeSoft")}
									</Label>
								</div>
							</div>
						</CardHeader>
						<CardContent className="space-y-3 pt-0">
							<div className="relative">
								<Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
								<Input
									placeholder={t("advancedSearch.skills.searchPlaceholder")}
									value={skillSearchQuery}
									onChange={(e) => setSkillSearchQuery(e.target.value)}
									className="pl-9 h-8 text-sm"
								/>
								{isSearchingSkills && (
									<Loader2 className="absolute right-3 top-1/2 transform -translate-y-1/2 h-3.5 w-3.5 animate-spin text-muted-foreground" />
								)}
								{skillSearchResults.length > 0 && (
									<div className="absolute top-full left-0 right-0 mt-1 bg-popover border border-border rounded-lg shadow-lg p-1.5 z-20 max-h-48 overflow-y-auto">
										{skillSearchResults.map((skill) => {
											const isAdded = recentlyAddedSkills.has(skill.id);
											return (
												<div
													key={skill.id}
													className={`px-2 py-1.5 hover:bg-accent rounded cursor-pointer text-sm flex items-center justify-between transition-colors ${isAdded ? 'bg-emerald-50 dark:bg-emerald-950/30' : ''}`}
													onClick={() => addSkillToSection(skill, 'available')}
												>
													<div className="flex items-center gap-2">
														{isAdded && <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600" />}
														<span className={isAdded ? 'text-emerald-700 dark:text-emerald-400 font-medium' : 'text-foreground'}>{skill.name}</span>
													</div>
													{skill.isSoft && <Badge variant="outline" className="text-[10px] h-5">{t("advancedSearch.skills.soft")}</Badge>}
												</div>
											);
										})}
									</div>
								)}
							</div>
							{renderSkillChips()}
						</CardContent>
					</Card>

					{/* Experiences */}
					<Card className="card-glass border-border">
						<CardHeader className="pb-3">
							<CardTitle className="text-sm font-medium text-foreground">{t("advancedSearch.experiences.title")}</CardTitle>
						</CardHeader>
						<CardContent className="space-y-3 pt-0">
							<div className="relative">
								<Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
								<Input
									placeholder={t("advancedSearch.experiences.searchPlaceholder")}
									value={experienceSearchQuery}
									onChange={(e) => setExperienceSearchQuery(e.target.value)}
									className="pl-9 h-8 text-sm"
								/>
								{isSearchingExperiences && (
									<Loader2 className="absolute right-3 top-1/2 transform -translate-y-1/2 h-3.5 w-3.5 animate-spin text-muted-foreground" />
								)}
								{experienceSearchResults.length > 0 && (
									<div className="absolute top-full left-0 right-0 mt-1 bg-popover border border-border rounded-lg shadow-lg p-1.5 z-20 max-h-48 overflow-y-auto">
										{experienceSearchResults.map((exp) => {
											const isAdded = recentlyAddedExperiences.has(exp.id);
											return (
												<div
													key={exp.id}
													className={`px-2 py-1.5 hover:bg-accent rounded cursor-pointer text-sm flex items-center justify-between transition-colors ${isAdded ? 'bg-emerald-50 dark:bg-emerald-950/30' : ''}`}
													onClick={() => addExperienceToSection(exp, 'available')}
												>
													<div className="flex items-center gap-2">
														{isAdded && <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600" />}
														<span className={isAdded ? 'text-emerald-700 dark:text-emerald-400 font-medium' : 'text-foreground'}>{exp.name}</span>
													</div>
													<Badge variant="outline" className="text-[10px] h-5">{exp.category.replace(/_/g, ' ')}</Badge>
												</div>
											);
										})}
									</div>
								)}
							</div>
							{renderExperienceChips()}
						</CardContent>
					</Card>
				</div>
			</div>

			{/* Action Bar */}
			<div className="flex items-center gap-3 pt-4 border-t border-border mt-4">
				<Button
					onClick={executeSearch}
					disabled={isSearching}
					className="flex-1 bg-blue-600 hover:bg-blue-700 text-white"
					size="lg"
				>
					{isSearching ? (
						<>
							<Loader2 className="h-4 w-4 mr-2 animate-spin" />
							{t("advancedSearch.actions.searching")}
						</>
					) : (
						<>
							<Search className="h-4 w-4 mr-2" />
							{t("candidateJobSearch.actions.findMatches")}
						</>
					)}
				</Button>
				<Button variant="outline" onClick={() => onOpenChange(false)} size="lg">
					<X className="h-4 w-4 mr-2" />
					{t("advancedSearch.actions.cancel")}
				</Button>
			</div>
		</div>
	);

	const renderResultsView = () => (
		<div className="flex flex-col h-full">
			{/* Header */}
			<div className="flex items-center justify-between pb-4 border-b border-border">
				<div className="flex items-center gap-3">
					<Button
						variant="ghost"
						size="sm"
						onClick={() => setCurrentView('config')}
						className="text-muted-foreground hover:text-foreground"
					>
						<ArrowLeft className="h-4 w-4 mr-1" />
						{t("candidateJobSearch.actions.backToCriteria")}
					</Button>
					<div className="h-4 w-px bg-border" />
					<h2 className="text-lg font-semibold text-foreground">
						{t("candidateJobSearch.results.title")}
					</h2>
					{searchResults?.shortlist && (
						<span className="text-sm text-muted-foreground">
							{t("candidateJobSearch.results.foundCount", { count: searchResults.shortlist.length })}
						</span>
					)}
				</div>

				{selectedJobs.size > 0 && (
					<Button
						onClick={handleCreateSelectedMatches}
						disabled={isCreatingMatches}
						className="bg-emerald-600 hover:bg-emerald-700 text-white"
						size="sm"
					>
						{isCreatingMatches ? (
							<Loader2 className="h-4 w-4 mr-2 animate-spin" />
						) : (
							<CheckCircle2 className="h-4 w-4 mr-2" />
						)}
						{t("candidateJobSearch.results.addSelected", { count: selectedJobs.size })}
					</Button>
				)}
			</div>

			{/* Content */}
			<div className="flex-1 overflow-y-auto scrollbar-thin pt-4">
				{isSearching && (
					<div className="flex flex-col items-center justify-center py-16">
						<div className="relative mb-8">
							<div className="absolute inset-0 -m-4 rounded-full border-2 border-blue-300/40 dark:border-blue-600/30 animate-ping" style={{ animationDuration: '3s' }} />
							<div className="absolute inset-0 -m-2 rounded-full border border-blue-200/50 dark:border-blue-700/30 animate-pulse" style={{ animationDuration: '2s' }} />
							<div className="h-24 w-24 rounded-full bg-blue-100 dark:bg-blue-950/50 flex items-center justify-center shadow-lg shadow-blue-200/50 dark:shadow-blue-900/30">
								<Brain className="h-12 w-12 text-blue-600 dark:text-blue-400 animate-pulse" />
							</div>
							<div
								className="absolute h-10 w-10 rounded-full bg-emerald-100 dark:bg-emerald-950/50 flex items-center justify-center shadow-md"
								style={{ top: '-8px', right: '-12px', animation: 'bounce 2.5s ease-in-out infinite' }}
							>
								<Filter className="h-5 w-5 text-emerald-600 dark:text-emerald-400" />
							</div>
							<div
								className="absolute h-9 w-9 rounded-full bg-amber-100 dark:bg-amber-950/50 flex items-center justify-center shadow-md"
								style={{ bottom: '-4px', left: '-14px', animation: 'bounce 2s ease-in-out infinite 0.5s' }}
							>
								<Zap className="h-5 w-5 text-amber-500 dark:text-amber-400" />
							</div>
							<div
								className="absolute h-8 w-8 rounded-full bg-purple-100 dark:bg-purple-950/50 flex items-center justify-center shadow-md"
								style={{ top: '50%', right: '-20px', marginTop: '-16px', animation: 'bounce 3s ease-in-out infinite 1s' }}
							>
								<Building2 className="h-4 w-4 text-purple-600 dark:text-purple-400" />
							</div>
						</div>

						<p className="text-base font-semibold text-foreground mb-1">{t("candidateJobSearch.progress.analyzing")}</p>
						<p className="text-xs text-muted-foreground mb-6">{t("advancedSearch.progress.patience")}</p>

						{searchProgress.length > 0 && (
							<div className="w-full max-w-md space-y-1.5">
								{searchProgress.filter(p => p.status === 'completed').map(progress => (
									<div key={progress.step} className="flex items-start gap-2 text-xs text-muted-foreground">
										<CheckCircle2 className="h-3.5 w-3.5 text-emerald-600 flex-shrink-0 mt-0.5" />
										<div className="flex-1 min-w-0">
											<span className="font-medium text-foreground">
												{t(`advancedSearch.progress.steps.${progress.step}`, { defaultValue: progress.step })}
											</span>
											{progress.data && (
												<span className="text-muted-foreground ml-1">
													({Object.entries(progress.data).map(([k, v]) =>
														`${t(`advancedSearch.progress.metrics.${k}`, { defaultValue: k })}: ${v}`
													).join(' \u2022 ')})
												</span>
											)}
										</div>
									</div>
								))}
								<div ref={progressEndRef} />
							</div>
						)}
					</div>
				)}

				{!isSearching && searchResults && (
					<div className="space-y-3">
						{searchResults.shortlist.length === 0 ? (
							<div className="text-center py-12 text-muted-foreground">
								{t("candidateJobSearch.noResults")}
							</div>
						) : (
							searchResults.shortlist.map((job) => {
								const matchedSkills = job.skillComparison.filter(s => s.matched);
								const matchedExperiences = job.experienceComparison.filter(e => e.matched);
								const isSelected = selectedJobs.has(job.jobId);

								return (
									<div
										key={job.jobId}
										className={`rounded-lg border p-4 cursor-pointer transition-colors ${
											job.isAlreadyMatched
												? 'border-emerald-200 bg-emerald-50/30 dark:bg-emerald-950/10 cursor-default'
												: isSelected
													? 'border-blue-400 bg-blue-50/50 dark:bg-blue-950/20'
													: 'border-border hover:border-blue-300 hover:bg-accent/30'
										}`}
										onClick={() => toggleJobSelection(job.jobId, job.isAlreadyMatched)}
									>
										<div className="flex items-start gap-3">
											{!job.isAlreadyMatched && (
												<Checkbox
													checked={isSelected}
													onCheckedChange={() => toggleJobSelection(job.jobId, false)}
													className="mt-0.5 flex-shrink-0"
													onClick={(e) => e.stopPropagation()}
												/>
											)}

											<div className="flex-1 min-w-0">
												<div className="flex items-center gap-2 flex-wrap mb-2">
													<span
											className="font-medium text-blue-600 dark:text-blue-400 hover:underline cursor-pointer"
											onClick={(e) => { e.stopPropagation(); window.open(`/jobs/${job.jobId}`, '_blank'); }}
										>
											{job.jobTitle}
										</span>
													{job.combinedScore >= 150 && (
														<Badge variant="outline" className="text-[10px] h-5 text-emerald-600 dark:text-emerald-400">
															{t("advancedSearch.results.highConfidence")}
														</Badge>
													)}
													{job.combinedScore >= 85 && job.combinedScore < 150 && (
														<Badge variant="outline" className="text-[10px] h-5 text-blue-600 dark:text-blue-400">
															{t("advancedSearch.results.strongConfidence")}
														</Badge>
													)}
													{job.isAlreadyMatched && (
														<Badge variant="outline" className="text-[10px] h-5 text-emerald-600 dark:text-emerald-400 border-emerald-300">
															{t("candidateJobSearch.results.alreadyMatched")}
														</Badge>
													)}
												</div>

												{jobSummaries[job.jobId] && (
													<p className="text-xs text-muted-foreground mb-2 line-clamp-2">{jobSummaries[job.jobId]}</p>
												)}

												{matchedSkills.length > 0 && (
													<div className="flex flex-wrap gap-1.5 mb-1">
														<span className="text-[10px] text-muted-foreground self-center">{t("advancedSearch.results.matchedSkills")}</span>
														{matchedSkills.slice(0, 4).map((skill, idx) => (
															<Badge key={idx} variant="outline" className="text-[10px] h-5 border-blue-200 bg-blue-50 dark:bg-blue-950/30 text-blue-700 dark:text-blue-300">
																{skill.candidateSkillName}
															</Badge>
														))}
														{matchedSkills.length > 4 && (
															<Badge variant="outline" className="text-[10px] h-5 text-muted-foreground">
																+{matchedSkills.length - 4}
															</Badge>
														)}
													</div>
												)}

												{matchedExperiences.length > 0 && (
													<div className="flex flex-wrap gap-1.5">
														<span className="text-[10px] text-muted-foreground self-center">{t("advancedSearch.results.similarExperiences")}</span>
														{matchedExperiences.slice(0, 3).map((exp, idx) => (
															<Badge key={idx} variant="outline" className="text-[10px] h-5 border-emerald-200 bg-emerald-50 dark:bg-emerald-950/30 text-emerald-700 dark:text-emerald-300">
																{exp.candidateExperienceName}
															</Badge>
														))}
														{matchedExperiences.length > 3 && (
															<Badge variant="outline" className="text-[10px] h-5 text-muted-foreground">
																+{matchedExperiences.length - 3}
															</Badge>
														)}
													</div>
												)}
											</div>
										</div>
									</div>
								);
							})
						)}
					</div>
				)}
			</div>
		</div>
	);

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent className="max-w-4xl w-full h-[85vh] flex flex-col p-6 gap-0">
				{currentView === 'config' ? renderConfigView() : renderResultsView()}
			</DialogContent>
		</Dialog>
	);
}
